import { useState } from "react";
import {
  Heading,
  Box,
  Button,
  useColorModeValue,
  HStack,
  Stack,
  useToast,
  Flex,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { GoPlus } from "react-icons/go";
import { Link as RoLink } from "react-router-dom";
import { filterQuestion, deleteQuestionsById } from "API/questionApi";
import { useQuery } from "react-query";
import useErrorToast from "hook/error-toast";
import AdminQuestionTable from "./QuestionTable/QuestionTable";
import SubjectSelect from "./SubjectSelect";
import LevelSelect from "./LevelSelect";
import QuestionTypeSelect from "./QuestionTypeSelect";
import { useAppDispatch, useAppSelector } from "app/hook";
import {
  decrementPage,
  incrementPage,
  setPageDefault,
  setSearchText,
  setSize,
  setSubject,
} from "app/features/questionFilter/questionFilterSlice";
import TableSearch from "components/dumb/Table/TableSearch";
import TablePaginator from "components/dumb/Table/TablePaginator";

const Questions = () => {
  const [resetCheckBox, setResetCheckBox] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { role } = useAppSelector((state) => state.auth);
  const { level, size, questionType, page, subject, searchText } =
    useAppSelector((state) => state.filterQuestion);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const { data, isLoading, error, refetch } = useQuery(
    ["questions", page, size, level, questionType, subject, searchText],
    () => {
      setResetCheckBox(true);
      return filterQuestion({
        page,
        size,
        questionType,
        level,
        subject,
        searchText,
      });
    },
    { onSettled: () => setResetCheckBox(false), keepPreviousData: true }
  );

  useErrorToast(error);

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;

    try {
      await deleteQuestionsById(selectedIds);
      toast({
        title: "Deleted",
        description: `${selectedIds.length} question(s) deleted successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setSelectedIds([]);
      setResetCheckBox(true);
      refetch();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || "Failed to delete questions",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Heading size="md" mb={4}>
        All Questions
      </Heading>

      <Box
        my="20px"
        border="1px"
        borderColor={useColorModeValue("gray.100", "gray.700")}
        borderTopRadius="md"
        pos="relative"
        p="15px"
      >
        
        <Stack
          w="100%"
          direction={{ base: "column", md: "row" }}
          spacing={4}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          mb={4}
        >
          
          <HStack spacing={3} flexWrap="wrap">
            <Box minW="200px">
              <TableSearch
                searchText={searchText}
                setPageDefault={setPageDefault}
                setSearchText={setSearchText}
              />
            </Box>
            <QuestionTypeSelect />
            <LevelSelect />
            <SubjectSelect
              subject={subject}
              selectOutput={(value) => {
                dispatch(setSubject(value));
                dispatch(setPageDefault());
              }}
            />
          </HStack>

          
          <HStack spacing={3} flexWrap="wrap">
            {selectedIds.length > 0 && (
              <Button
                leftIcon={<FaTrash />}
                colorScheme="red"
                variant="solid"
                size="sm"
                onClick={handleDelete}
              >
                Delete Selected
              </Button>
            )}

            <Button
              rightIcon={<GoPlus />}
              colorScheme="teal"
              variant="solid"
              size="sm"
              as={RoLink}
              to={`/${role}/dashboard/import-questions`}
            >
              Add Question
            </Button>

            <Button
              colorScheme="blue"
              variant="outline"
              size="sm"
              as={RoLink}
              to={`/${role}/dashboard/manual-insert-question`}
            >
              Insert Manually
            </Button>
          </HStack>
        </Stack>

        
        <AdminQuestionTable
          resetCheckBox={resetCheckBox}
          items={data?.questions}
          isLoading={isLoading}
          disableUpdate={false}
          onSelectedChange={setSelectedIds}
        />

       
        <TablePaginator
          count={data?.count}
          decrementPage={decrementPage}
          incrementPage={incrementPage}
          page={data?.page}
          setPageDefault={setPageDefault}
          setSize={setSize}
          size={size}
          totalPages={data?.totalPage}
        />
      </Box>
    </>
  );
};

export default Questions;
